import React, { useMemo, useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { COLORS, formatKWh, formatEuros } from './ChartUtils';
import { InfoBox } from '../Components';
import { t } from '../i18n';

const ConsumptionDevicesPieChart = ({ data }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Process data for consumption pie chart
  const consumptionData = useMemo(() => {
    const chartData = data?.metadata?.device_breakdown?.consumption?.chart_data;

    if (!chartData || !Array.isArray(chartData)) {
      return [];
    }

    return chartData
      .filter(item => Number(item.consumption) > 0)
      .map((item, index) => ({
        name: item.name,
        value: Number(item.consumption) || 0,
        cost: Number(item.cost) || 0,
        percentage: Number(item.percentage) || 0,
        device_count: item.device_count,
        color: COLORS[index % COLORS.length]
      }));
  }, [data]);

  // Process data for cost pie chart (same order as consumption)
  const costData = useMemo(() => {
    if (!consumptionData.length) return [];

    const totalCost = consumptionData.reduce((sum, item) => sum + item.cost, 0);

    return consumptionData
      .filter(item => item.cost > 0)
      .map(item => ({
        name: item.name,
        value: item.cost,
        consumption: item.value,
        percentage: totalCost > 0 ? (item.cost / totalCost * 100) : 0,
        device_count: item.device_count,
        color: item.color
      }));
  }, [consumptionData]);

  // Get totals from metadata
  const totals = useMemo(() => {
    return data?.metadata?.device_breakdown?.consumption?.totals || null;
  }, [data]);

  if (!consumptionData.length) return null;

  // InfoBox data
  const infoBoxData = totals ? [
    { label: t('monthly.totalConsumption'), value: formatKWh(totals.total_consumption, 1) },
    { label: t('monthly.totalCost'), value: formatEuros(totals.total_cost) },
    { label: t('monthly.deviceCount'), value: totals.device_count }
  ] : [];

  // Responsive sizing - matching PieCharts_V2
  const dynamicChartHeight = isMobile ? 500 : 450;
  const dynamicOuterRadius = isMobile ? 100 : 140;
  const dynamicLegendLayout = isMobile ? "horizontal" : "vertical";
  const dynamicAlign = isMobile ? "center" : "right";
  const dynamicVerticalAlign = isMobile ? "bottom" : "middle";
  const dynamicLegendWrapperStyle = {
    marginTop: 10,
    whiteSpace: "normal",
    maxWidth: "100%",
  };

  // Custom legend formatter for consumption
  const renderConsumptionLegend = (value, entry) =>
    `${entry.payload.name}: ${entry.payload.value.toFixed(2)}`;

  // Custom legend formatter for cost (in euros)
  const renderCostLegend = (value, entry) =>
    `${entry.payload.name}: ${(entry.payload.value / 100).toFixed(2)}`;

  return (
    <div>
      {/* InfoBox with summary data */}
      {totals && (
        <InfoBox title={t('monthly.consumptionSummary')} dataPoints={infoBoxData} />
      )}

      {/* Two pie charts side by side - matching daily report layout */}
      <div className="pie-chart-container">
        {/* Consumption Pie Chart */}
        <div className="pie-chart">
          <h4>{t('monthly.consumptionByDevice')}</h4>
          <ResponsiveContainer width="100%" height={dynamicChartHeight}>
            <PieChart>
              <Pie
                data={consumptionData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={dynamicOuterRadius}
              >
                {consumptionData.map((entry, index) => (
                  <Cell key={`cell-consumption-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Legend
                layout={dynamicLegendLayout}
                verticalAlign={dynamicVerticalAlign}
                align={dynamicAlign}
                wrapperStyle={dynamicLegendWrapperStyle}
                formatter={renderConsumptionLegend}
              />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Cost Pie Chart */}
        <div className="pie-chart">
          <h4>{t('monthly.costByDeviceEuros')}</h4>
          <ResponsiveContainer width="100%" height={dynamicChartHeight}>
            <PieChart>
              <Pie
                data={costData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={dynamicOuterRadius}
              >
                {costData.map((entry, index) => (
                  <Cell key={`cell-cost-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Legend
                layout={dynamicLegendLayout}
                verticalAlign={dynamicVerticalAlign}
                align={dynamicAlign}
                wrapperStyle={dynamicLegendWrapperStyle}
                formatter={renderCostLegend}
              />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ConsumptionDevicesPieChart;
